import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { BLOG_POSTS } from "@/data/blogPosts";
import { useSeo } from "@/lib/useSeo";

const LOGO = "/assets/logo.png";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

function BlogHeader() {
  return (
    <header className="border-b border-zinc-900">
      <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="blog-home-link">
          <img src={LOGO} alt="Social Media Combo logo" className="w-8 h-8 object-contain" />
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Social Media Combo</span>
            <span className="font-serif-display text-base tracking-[0.25em] font-semibold">
              THE FACELESS BUNDLE<span className="text-[#D4AF37]">.</span>
            </span>
          </div>
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to store
        </Link>
      </div>
    </header>
  );
}

export default function Blog() {
  useSeo({
    title: "Faceless Growth Insights — The Blog | Social Media Combo",
    description:
      "Strategies, psychology, and playbooks for building a profitable faceless brand on Instagram, TikTok and YouTube — from the team behind The Faceless Bundle.",
    path: "/blog",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <BlogHeader />

      <main className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        <p className="text-sm uppercase tracking-[0.25em] text-[#D4AF37]">The Blog</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-bold mt-4">
          Faceless Growth Insights
        </h1>
        <p className="mt-4 text-lg text-zinc-400 max-w-2xl">
          Strategies, psychology, and playbooks for building a profitable faceless brand on social media.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="blog-list">
          {BLOG_POSTS.map((post, i) => (
            <motion.article
              key={post.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`blog-card-${i}`}
            >
              <Link to={`/blog/${post.slug}`} className="group block bg-[#121212] border border-zinc-800 hover:border-[#D4AF37]/50 transition-colors overflow-hidden">
                <div className="aspect-[16/9] overflow-hidden bg-[#1C1C1E]">
                  <img
                    src={post.cover}
                    alt={post.coverAlt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-3 text-xs text-zinc-500">
                    <span className="uppercase tracking-widest text-[#D4AF37]">{post.tag}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
                  </div>
                  <h2 className="mt-4 font-serif-display text-xl font-semibold leading-snug group-hover:text-[#D4AF37] transition-colors">
                    {post.title}
                  </h2>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{post.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-xs text-zinc-500">{fmtDate(post.date)}</span>
                    <span className="inline-flex items-center gap-1 text-sm text-[#D4AF37]">
                      Read <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </main>

      <footer className="border-t border-zinc-900 py-10">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-600">
          <span className="font-serif-display tracking-widest">SOCIAL MEDIA COMBO.</span>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[#D4AF37] transition-colors">Terms</Link>
            <span>© {new Date().getFullYear()} socialmediacombo.com</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
